import { useEffect, useState } from "react";
import { Plus, Video, Megaphone, Loader2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

export default function Meetings() {
    const { user } = useAuth();
    const [meetings, setMeetings] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [openM, setOpenM] = useState(false);
    const [openA, setOpenA] = useState(false);
    const canSchedule = ["super_admin", "hr", "manager", "lead"].includes(user?.role);
    const canBroadcast = ["super_admin", "hr"].includes(user?.role);

    const load = async () => {
        try {
            const [m, a, emp] = await Promise.all([
                api.get("/meetings"),
                api.get("/announcements"),
                api.get("/employees?limit=500&status=active")
            ]);
            setMeetings(m.data.items || []);
            setAnnouncements(a.data.items || []);
            setEmployees(emp.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { load(); }, []);

    return (
        <div>
            <PageHeader
                title="Meetings & Announcements"
                subtitle="Schedule meetings and broadcast to the team."
                testId="meetings-header"
                actions={
                    <div className="flex gap-2">
                        {canSchedule && (
                            <Dialog open={openM} onOpenChange={setOpenM}>
                                <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="new-meeting-btn"><Plus className="w-4 h-4 mr-1" /> Meeting</Button></DialogTrigger>
                                <DialogContent><DialogHeader><DialogTitle>Schedule meeting</DialogTitle></DialogHeader><MeetingForm employees={employees} onCreated={() => { setOpenM(false); load(); }} /></DialogContent>
                            </Dialog>
                        )}
                        {canBroadcast && (
                            <Dialog open={openA} onOpenChange={setOpenA}>
                                <DialogTrigger asChild><Button className="rounded-full" data-testid="new-announcement-btn"><Megaphone className="w-4 h-4 mr-1" /> Announcement</Button></DialogTrigger>
                                <DialogContent><DialogHeader><DialogTitle>New announcement</DialogTitle></DialogHeader><AnnouncementForm onCreated={() => { setOpenA(false); load(); }} /></DialogContent>
                            </Dialog>
                        )}
                    </div>
                }
            />

            <Tabs defaultValue="meetings">
                <TabsList>
                    <TabsTrigger value="meetings" data-testid="tab-meetings">Meetings</TabsTrigger>
                    <TabsTrigger value="announcements" data-testid="tab-announcements">Announcements</TabsTrigger>
                </TabsList>
                <TabsContent value="meetings" className="mt-4">
                    {meetings.length === 0 ? (
                        <GlassCard><EmptyState icon={Video} title="No meetings yet" description="Schedule a meeting to bring the team together." /></GlassCard>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {meetings.map((m) => (
                                <GlassCard key={m.id} className="p-5 glass-hover">
                                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{new Date(m.scheduled_at).toLocaleString()}</div>
                                    <h4 className="font-heading font-semibold text-lg mt-1">{m.title}</h4>
                                    {m.description && <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{m.description}</p>}
                                    <div className="flex items-center justify-between mt-4">
                                        <span className="text-xs text-muted-foreground">{m.duration_minutes} min · by {m.organizer}</span>
                                        {m.meet_link && <a href={m.meet_link} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">Join →</a>}
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </TabsContent>
                <TabsContent value="announcements" className="mt-4">
                    {announcements.length === 0 ? (
                        <GlassCard><EmptyState icon={Megaphone} title="No announcements" /></GlassCard>
                    ) : (
                        <div className="space-y-3">
                            {announcements.map((a) => (
                                <GlassCard key={a.id} className="p-5">
                                    <div className="flex justify-between gap-3">
                                        <div>
                                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">For {a.audience}</div>
                                            <h4 className="font-heading font-semibold text-lg mt-1">{a.title}</h4>
                                            <p className="text-sm text-muted-foreground mt-2">{a.body}</p>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground whitespace-nowrap">{new Date(a.created_at).toLocaleDateString()}</div>
                                    </div>
                                </GlassCard>
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}

function MeetingForm({ employees = [], onCreated }) {
    const [form, setForm] = useState({ title: "", description: "", scheduled_at: "", duration_minutes: 30, meet_link: "" });
    const [selectedAttendees, setSelectedAttendees] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/meetings", { 
                ...form, 
                attendees: selectedAttendees.map(x => x.id) 
            });
            toast.success("Meeting scheduled");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="meeting-title" /></div>
            <div><Label>When</Label><Input required type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1" data-testid="meeting-when" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><Label>Duration (min)</Label><Input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} className="mt-1" /></div>
                <div><Label>Meet link</Label><Input value={form.meet_link} onChange={(e) => setForm({ ...form, meet_link: e.target.value })} placeholder="https://meet.google.com/…" className="mt-1" /></div>
            </div>
            
            <div>
                <Label>Add Participants</Label>
                <select 
                    onChange={(e) => {
                        const val = e.target.value;
                        if (!val) return;
                        const emp = employees.find(x => x.id === val);
                        if (emp && !selectedAttendees.find(x => x.id === val)) {
                            setSelectedAttendees(prev => [...prev, emp]);
                        }
                        e.target.value = "";
                    }}
                    className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm"
                >
                    <option value="">Select employee / intern to add...</option>
                    {employees.map(emp => (
                        <option key={emp.id} value={emp.id}>
                            {emp.name} ({emp.designation || emp.role})
                        </option>
                    ))}
                </select>
            </div>

            {selectedAttendees.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                    {selectedAttendees.map(att => (
                        <span 
                            key={att.id} 
                            className="inline-flex items-center gap-1 bg-teal-50 dark:bg-teal-950/20 border border-teal-150 text-teal-800 dark:text-teal-400 px-2.5 py-0.5 rounded-full text-[10px] font-semibold"
                        >
                            {att.name}
                            <button 
                                type="button" 
                                onClick={() => setSelectedAttendees(prev => prev.filter(x => x.id !== att.id))}
                                className="text-teal-600 dark:text-teal-400 hover:text-red-500 font-bold ml-1 text-xs"
                            >
                                ✕
                            </button>
                        </span>
                    ))}
                </div>
            )}

            <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="meeting-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schedule"}</Button>
        </form>
    );
}

function AnnouncementForm({ onCreated }) {
    const [form, setForm] = useState({ title: "", body: "", audience: "all" });
    const [loading, setLoading] = useState(false);
    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/announcements", form);
            toast.success("Announcement sent");
            onCreated();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    return (
        <form onSubmit={submit} className="space-y-3">
            <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" data-testid="ann-title" /></div>
            <div><Label>Audience</Label>
                <select value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-input bg-background px-3 text-sm" data-testid="ann-audience">
                    <option value="all">All</option>
                    <option value="hr">HR</option>
                    <option value="managers">Managers</option>
                    <option value="employees">Employees</option>
                    <option value="interns">Interns</option>
                </select>
            </div>
            <div><Label>Message</Label><Textarea required rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1" data-testid="ann-body" /></div>
            <Button type="submit" className="w-full rounded-full h-11" disabled={loading} data-testid="ann-submit">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}</Button>
        </form>
    );
}
